import { HttpClient } from '@adapters/HttpClient';
import { loadBatchesForMultiSelect as fetchBatchesForMultiSelect } from '@modules/Reports/utils/loadBatchesForMultiSelect';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';

type PaginatedListParams = { search: string; page: number; limit: number };

function mapMetaHasMore(response: { meta?: { currentPage: number; lastPage: number } }): boolean {
  const meta = response.meta;
  return meta ? meta.currentPage < meta.lastPage : false;
}

function listFromResponse(response: unknown): any[] {
  if (Array.isArray(response)) return response;
  const data = (response as { data?: unknown })?.data;
  return Array.isArray(data) ? data : [];
}

export class StockMovementsFiltersApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async loadTeams(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const queryParams: Record<string, number | string> = {
        page: params.page,
        per_page: params.limit,
        'sorting[column]': 'name',
        'sorting[direction]': 'asc',
      };
      if (params.search.trim()) {
        queryParams.name = params.search.trim();
      }
      const response = await this.httpClient.get<{
        data?: { id: number; name: string }[];
        meta?: { currentPage?: number; lastPage?: number; current_page?: number; last_page?: number };
      }>('/teams-child', queryParams);
      const list = Array.isArray(response) ? response : (response.data ?? []);
      const meta = Array.isArray(response) ? undefined : response.meta;
      const currentPage = Number(meta?.currentPage ?? meta?.current_page ?? 1);
      const lastPage = Number(meta?.lastPage ?? meta?.last_page ?? 1);
      return {
        options: list.map((team) => ({
          value: String(team.id),
          label: team.name || `#${team.id}`,
        })),
        hasMore: meta ? currentPage < lastPage : false,
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }

  async loadBatches(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    return fetchBatchesForMultiSelect(this.httpClient, params);
  }

  async loadOrders(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const response = await this.httpClient.get<any>('/orders', {
        search: params.search || undefined,
        page: params.page,
        per_page: params.limit,
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
      });
      const list = listFromResponse(response);
      return {
        options: list.map((order: { id: number; ref?: string }) => ({
          value: String(order.id),
          label: order.ref || `#${order.id}`,
        })),
        hasMore: mapMetaHasMore(response),
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }

  async loadPreparations(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const response = await this.httpClient.get<any>('/preparations', {
        search: params.search || undefined,
        page: params.page,
        per_page: params.limit,
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
      });
      const list = listFromResponse(response);
      return {
        options: list.map((prep: { id: number; code?: string }) => ({
          value: String(prep.id),
          label: prep.code || `PREP-${prep.id}`,
        })),
        hasMore: mapMetaHasMore(response),
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }

  async loadReturnedReceptions(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const response = await this.httpClient.get<any>('/returned-receptions', {
        search: params.search || undefined,
        page: params.page,
        per_page: params.limit,
        order_by: 'id',
        order_direction: 'desc',
      });
      const list = listFromResponse(response);
      return {
        options: list.map((item: { id: number; code?: string }) => ({
          value: String(item.id),
          label: item.code || `RR-${item.id}`,
        })),
        hasMore: mapMetaHasMore(response),
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }

  async loadEntries(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const response = await this.httpClient.get<any>('/entries', {
        search: params.search || undefined,
        page: params.page,
        per_page: params.limit,
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
      });
      const list = listFromResponse(response);
      return {
        options: list.map((entry: { id: number; reference?: string }) => ({
          value: String(entry.id),
          label: entry.reference || `ENT-${entry.id}`,
        })),
        hasMore: mapMetaHasMore(response),
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }

  async loadExtraWorkOrders(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const response = await this.httpClient.get<any>('/extra-work-orders', {
        search: params.search || undefined,
        page: params.page,
        per_page: params.limit,
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
      });
      const list = listFromResponse(response);
      return {
        options: list.map((order: { id: number; ref?: string }) => ({
          value: String(order.id),
          label: order.ref || `EWO-${order.id}`,
        })),
        hasMore: mapMetaHasMore(response),
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }

  async loadLocationOrders(params: PaginatedListParams): Promise<LoadOptionsResponse> {
    try {
      const response = await this.httpClient.get<any>('/location-orders', {
        search: params.search || undefined,
        page: params.page,
        per_page: params.limit,
      });
      const list = listFromResponse(response);
      return {
        options: list.map((item: { id: number }) => ({
          value: String(item.id),
          label: `#${item.id}`,
        })),
        hasMore: mapMetaHasMore(response),
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }
}
